import { useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * SuggestCaptionButton — one-tap "let Willoah draft a caption" button.
 *
 * Reads the current draft (title / category / context / summary) via the
 * `draft` prop, POSTs to /api/ideas/suggest-caption, and hands the sanitised
 * string back through `onSuggest` so the parent form can drop it into its
 * `summary` field.
 *
 * Deliberately dumb + framework-free: the parent owns the input state,
 * we just fetch a string and hand it over. Placed next to the caption
 * input on PlantIdea (wizard + recipe form) and IdeaEditPanel.
 */
export default function SuggestCaptionButton({ draft, onSuggest, disabled, testid = "suggest-caption-btn" }) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (busy || disabled) return;
    if (!draft?.title?.trim()) {
      toast("Add a title first — the caption needs something to work from.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ideas/suggest-caption", {
        title: draft.title || "",
        category: draft.category || "",
        context: draft.context || "",
      });
      const caption = (data?.caption || "").trim();
      if (!caption) {
        toast("The model came back empty — try again.");
        return;
      }
      onSuggest(caption);
    } catch (err) {
      // 503 = key missing / model unavailable, 502 = model call failed —
      // show whatever message the server gave us so people know it's not
      // their fault.
      const detail = err?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Couldn't draft a caption right now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || disabled}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs text-[#4E5A3D] hover:text-[#3E4830] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      title="Ask Willoah to draft a caption"
    >
      <Sparkles className={`w-3.5 h-3.5 ${busy ? "animate-pulse" : ""}`} strokeWidth={1.75} />
      {busy ? "Drafting…" : "Suggest a caption"}
    </button>
  );
}
